import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  InputBase,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import {
  CheckCircleOutline as ReadyIcon,
  Close as RemoveIcon,
  ExpandMoreRounded as ExpandIcon,
  WarningAmberRounded as WarningIcon,
} from '@material-ui/icons'
import FileNameText from 'src/components/FileNameText'
import HPOFilterWrapper from 'src/components/HPOFilterWrapper'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import SampleEditor, { NumberInput } from './SampleEditor'
import NormalSelect from './components/NormalSelect'
import IssueList from './components/IssueList'
import TumourTypeSelect from './components/TumourTypeSelect'
import LongReadOptions from './components/LongReadOptions'
import HpoSuggestions, { SETTLE_MS, prefetchHpoSuggestions } from './components/HpoSuggestions'
import {
  CELL_ROW_ATTR,
  GroupLabel,
  MONO_SX,
  Muted,
  Segmented,
  TextLink,
  cellButtonSx,
  cellInputSx,
} from './components/ui'
import { TABLE_ATTR, moveInColumn } from './components/cellNav'
import { NOTES_MAX_LENGTH, lanesAllowed } from 'src/services/analysisStart'
import {
  SEX_OPTIONS,
  aiPatch,
  canSavePatient,
  canUseAI,
  describeFiles,
  effectiveAI,
  hasHistory,
  hasPhenotype,
  hpoCount,
  parseAge,
  phenotypeSummary,
  sexShortLabel,
} from './model/samples'
import { isTumourNormal, longReadApplies } from './model/workflow'
import { fromTerms, toTerms } from './model/hpo'
import { UNKNOWN_PRICE_TEXT, formatCredits, partsText, priceOf, usePrices } from './model/pricing'
import { downloadTemplate } from './model/template'

// The batch table for 2 or more samples. The workflow is set once in the left
// column; this table is for the few values that differ per sample. A row's
// issues sit on a line under it, each with its fix.
//
// AI is chosen per sample. While any sample can run AI-assisted (the batch is
// AI-assisted, or a sample was switched on), the germline table shows an AI
// checkbox, the HPO count and the clinical history of every row, with a hint line
// above; Notes then move to the row's details. Every row shows its credits.
//
// Long-read (ONT, PacBio) files carry their patient details on the file, and they
// cannot be saved from here: their Sex and Age show as plain text. Each long-read
// row can have its own CNV, SV and Methylation options in its details; a row with
// its own options is marked "Custom".

const READ_ONLY_TITLE = 'Saved on the file. Read-only here.'

// True when a sample has its own long-read options (sample.longRead).
export const hasLongReadOverride = (sample) =>
  Boolean(sample?.longRead && typeof sample.longRead === 'object' && Object.keys(sample.longRead).length)

// Issues that the row already shows in a cell, so they get no line of their own.
const SHOWN_IN_ROW = new Set(['verifying'])
const NO_ISSUES = []
const NO_OPTIONS = []

// Widths are set so the table fits the samples area of a 1280px laptop screen
// (about 740px) without scrolling sideways, in every layout.
const COLUMN_GAP_PX = 8
const ROW_PX = 8
export const columnsFor = (workflow, { ai = false, credits = false } = {}) => {
  const somatic = workflow.analysis === 'somatic'
  const tn = isTumourNormal(workflow)
  const withAI = ai && !somatic
  const cols = [
    { id: 'select', width: '28px' },
    { id: 'status', width: '20px' },
    {
      id: 'sample',
      label: tn ? 'Tumour' : 'Sample',
      width: tn ? 'minmax(112px, 1.2fr)' : withAI ? 'minmax(100px, 1.2fr)' : 'minmax(118px, 1.3fr)',
    },
  ]
  if (tn) cols.push({ id: 'normal', label: 'Normal', width: 'minmax(106px, 1fr)' })
  else cols.push({ id: 'files', label: 'Files', width: withAI ? '64px' : '72px' })
  // A somatic run reads no age and infers sex from the reads.
  if (!somatic) {
    cols.push({ id: 'sex', label: 'Sex', width: '92px' })
    cols.push({ id: 'age', label: 'Age', width: '48px' })
  }
  if (somatic) cols.push({ id: 'tumour', label: 'Tumour type', width: '96px' })
  else if (withAI) {
    cols.push({ id: 'ai', label: 'AI', width: '46px' })
    cols.push({ id: 'hpoCount', label: 'HPO', width: '52px' })
    cols.push({ id: 'history', label: 'Clinical history', width: 'minmax(98px, 1.6fr)' })
  } else cols.push({ id: 'hpo', label: 'Phenotype', width: '108px' })
  // With the AI columns, Notes move to the row's details.
  if (!withAI) cols.push({ id: 'notes', label: 'Notes', width: tn ? 'minmax(66px, 1fr)' : 'minmax(70px, 1fr)' })
  if (credits) cols.push({ id: 'credits', label: 'Credits', width: '52px' })
  // Details and Remove share the last column.
  cols.push({ id: 'expand', width: '44px' })
  return cols
}

// Issue lines and the details line up with the sample name.
const SAMPLE_START_PX = ROW_PX + 28 + COLUMN_GAP_PX + 20 + COLUMN_GAP_PX
// Headers over inputs and cell buttons line up with the text inside them.
const PADDED_HEADERS = new Set(['sex', 'age', 'hpo', 'hpoCount', 'history', 'tumour', 'notes'])
const RIGHT_HEADERS = new Set(['credits'])

export const AI_HINT = 'AI-assisted needs HPO terms and clinical history for each AI sample.'
const NO_AI_TITLE = 'AI reads SNV/Indel VCFs only'

const gridOf = (cols) => cols.map((c) => c.width).join(' ')

const StatusIcon = ({ issues, fileState }) => {
  const blocking = issues.filter((i) => i.blocking)
  if (fileState === 'verifying' && blocking.every((i) => i.code === 'verifying')) {
    return (
      <Tooltip title="Files are still being verified">
        <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary' }} />
      </Tooltip>
    )
  }
  if (blocking.length) {
    return (
      <Tooltip title={blocking.map((i) => i.text).join('. ')}>
        <WarningIcon sx={{ fontSize: 18, color: 'text.primary' }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title="Ready">
      <ReadyIcon sx={{ fontSize: 18, color: 'success.main' }} />
    </Tooltip>
  )
}

const iconButtonSx = {
  width: 22,
  height: 22,
  flexShrink: 0,
  borderRadius: '50%',
  color: 'text.secondary',
  '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
  '&.Mui-focusVisible': { opacity: 1, outline: '2px solid', outlineColor: 'primary.main' },
}

const SampleRow = React.memo(function SampleRow({
  sample,
  index,
  cols,
  grid,
  workflow,
  fileMap,
  issues,
  selected,
  expanded,
  onToggleSelect,
  onToggleExpand,
  onRemove,
  onChange,
  onFix,
  normalOptions,
  prices,
  aiColumns,
  onHoldHpo,
  bedFiles,
  rnaCapture,
}) {
  const theme = useTheme()
  const files = describeFiles(sample, fileMap)
  const change = (patch) => onChange(sample.uid, patch)
  const setPatient = (field, value) => change({ [field]: value, changed: { ...(sample.changed || {}), [field]: true } })
  const patientEditable = canSavePatient(sample)
  const longRead = longReadApplies(workflow)
  const custom = longRead && hasLongReadOverride(sample)
  const lineIssues = issues.filter((i) => !SHOWN_IN_ROW.has(i.code))
  const blocked = issues.some((i) => i.blocking)
  const tumour = sample.tumourType || workflow.tumourType
  const inputSx = cellInputSx(theme)
  const buttonSx = cellButtonSx(theme)
  const phenotype = phenotypeSummary(sample)
  const aiOk = canUseAI(workflow, sample)
  const aiOn = effectiveAI(workflow, sample)
  const hpoN = hpoCount(sample)

  // The HPO cell adds terms in a small popover, without opening the row.
  const hpoCellRef = useRef(null)
  const [hpoOpen, setHpoOpen] = useState(false)
  const hpoTerms = useMemo(() => toTerms(sample.hpo || []), [sample.hpo])
  const hpoCellInputId = `hpo-cell-input-${sample.uid}`
  const historyInputId = `history-input-${sample.uid}`
  // The row stays in a filtered view while its HPO terms are open.
  useEffect(() => {
    if (!hpoOpen || !onHoldHpo) return undefined
    onHoldHpo(sample.uid, true)
    return () => onHoldHpo(sample.uid, false)
  }, [hpoOpen, sample.uid, onHoldHpo])

  // The history cell shows no suggestions of its own: once the typed text settles
  // they are asked for, as the old wizard did, so the HPO cell opens on them.
  const prefetchTimer = useRef(null)
  const prefetchOnSettle = (text) => {
    clearTimeout(prefetchTimer.current)
    prefetchTimer.current = setTimeout(() => prefetchHpoSuggestions(text), SETTLE_MS)
  }
  useEffect(() => () => clearTimeout(prefetchTimer.current), [])

  // The Phenotype and Tumour type cells open the details and put the cursor in
  // their field.
  const focusOnOpen = useRef(null)
  const openDetails = (inputId) => {
    if (expanded) {
      document.getElementById(inputId)?.focus()
      return
    }
    focusOnOpen.current = inputId
    onToggleExpand(sample.uid)
  }
  useEffect(() => {
    const inputId = focusOnOpen.current
    focusOnOpen.current = null
    if (expanded && inputId) document.getElementById(inputId)?.focus()
  }, [expanded])

  // "Clear" on an invalid age and "Run without AI" are handled here. With the AI
  // columns, "Add phenotype" goes to the one thing missing: the HPO popover or the
  // clinical history cell. The rest go to the panel.
  // The full single-sample editor in a dialog: the inline one leaves out the
  // specimen, normal and RNA library fields a somatic start needs.
  const [detailsOpen, setDetailsOpen] = useState(false)
  const somaticRow = workflow.analysis === 'somatic'

  const fixIssue = (fixName, issue) => {
    if (fixName === 'details') setDetailsOpen(true)
    else if (fixName === 'clear' && issue?.field) setPatient(issue.field, '')
    else if (fixName === 'aiOff' || fixName === 'noAI') change(aiPatch(workflow, false))
    else if (fixName === 'addPhenotype' && aiColumns && hpoN > 0 && !hasHistory(sample)) {
      document.getElementById(historyInputId)?.focus()
    } else if (fixName === 'addPhenotype' && aiColumns && hpoN === 0 && hasHistory(sample)) setHpoOpen(true)
    else onFix(sample.uid, fixName, issue)
  }

  const cell = (col) => {
    switch (col.id) {
      case 'select':
        return (
          <Checkbox
            size="small"
            checked={selected}
            onChange={() => onToggleSelect(sample.uid)}
            inputProps={{ 'aria-label': `Select ${sample.key}` }}
            sx={{ p: 0.5 }}
          />
        )
      case 'status':
        return <StatusIcon issues={issues} fileState={files.state} />
      case 'sample':
        return (
          <ButtonBase
            onClick={() => onToggleExpand(sample.uid)}
            aria-expanded={expanded}
            sx={{ display: 'block', textAlign: 'left', width: '100%', minWidth: 0, borderRadius: 0.5, py: 0.5 }}
          >
            <FileNameText name={sample.key} variant="plain" maxLines={2} sx={{ fontSize: 14, fontWeight: 500 }} />
          </ButtonBase>
        )
      case 'normal':
        return <NormalSelect sample={sample} options={normalOptions} onChange={change} compact />
      case 'files':
        return (
          <Tooltip title={[files.summary, ...files.names].join('\n')} placement="bottom-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.35 }} noWrap>
                {files.state === 'verifying' ? 'Verifying' : files.short}
              </Typography>
              {custom && (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.35 }} noWrap>
                  Custom
                </Typography>
              )}
            </Box>
          </Tooltip>
        )
      case 'sex':
        if (!patientEditable) {
          return (
            <Typography
              title={READ_ONLY_TITLE}
              aria-label={`Sex of ${sample.key}: ${sexShortLabel(sample.sex)}`}
              sx={{ fontSize: 13.5, color: sample.sex ? 'text.primary' : 'text.secondary', pl: '7px' }}
              noWrap
            >
              {sexShortLabel(sample.sex)}
            </Typography>
          )
        }
        return (
          <Select
            value={sample.sex || ''}
            onChange={(e) => setPatient('sex', e.target.value)}
            disabled={!patientEditable}
            displayEmpty
            renderValue={(v) => (
              <Typography component="span" sx={{ fontSize: 13.5, color: v ? 'text.primary' : 'text.secondary' }}>
                {sexShortLabel(v)}
              </Typography>
            )}
            input={
              <InputBase
                sx={{
                  ...inputSx,
                  pr: 0,
                  '& .MuiSelect-select': { py: 0, pr: '24px !important' },
                  '& .MuiSelect-icon': { right: 2 },
                }}
              />
            }
            inputProps={{ 'aria-label': `Sex of ${sample.key}` }}
          >
            {SEX_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'unknown'} value={o.value} sx={{ fontSize: 14 }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        )
      case 'age':
        if (!patientEditable) {
          const age = sample.age === null || sample.age === undefined ? '' : String(sample.age)
          return (
            <Typography
              title={READ_ONLY_TITLE}
              aria-label={`Age of ${sample.key}: ${age || 'none'}`}
              sx={{ fontSize: 13.5, pl: '7px', fontVariantNumeric: 'tabular-nums' }}
              noWrap
            >
              {age}
            </Typography>
          )
        }
        return (
          <NumberInput
            cell
            value={sample.age}
            onChange={(v) => setPatient('age', v)}
            onKeyDown={moveInColumn}
            inputProps={{ 'aria-label': `Age of ${sample.key}`, 'data-col': 'age', 'data-row': index }}
            error={!!sample.sheetErrors?.age}
            disabled={!patientEditable}
          />
        )
      case 'ai':
        if (!aiOk) {
          return (
            <Tooltip title={NO_AI_TITLE}>
              <span>
                <Checkbox
                  size="small"
                  disabled
                  checked={false}
                  inputProps={{ 'aria-label': `AI for ${sample.key}` }}
                  sx={{ p: 0.5 }}
                />
              </span>
            </Tooltip>
          )
        }
        return (
          <Checkbox
            size="small"
            checked={aiOn}
            onChange={(e) => change(aiPatch(workflow, e.target.checked))}
            inputProps={{ 'aria-label': `AI for ${sample.key}` }}
            sx={{ p: 0.5 }}
          />
        )
      case 'hpoCount':
        return (
          <ButtonBase
            ref={hpoCellRef}
            onClick={() => setHpoOpen(true)}
            aria-haspopup="dialog"
            aria-label={`HPO terms of ${sample.key}: ${hpoN || 'none'}`}
            sx={buttonSx}
          >
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: 13.5,
                fontWeight: hpoN ? 400 : 500,
                fontVariantNumeric: 'tabular-nums',
                // Needed for AI: the empty cell asks in the primary colour.
                color: hpoN ? 'text.primary' : aiOn ? 'primary.main' : 'text.secondary',
              }}
            >
              {hpoN || 'Add'}
            </Typography>
          </ButtonBase>
        )
      case 'history':
        // One line of the clinical history; the row's details show all of it. A
        // textarea, so line breaks written in the details are kept.
        return (
          <InputBase
            multiline
            rows={1}
            value={sample.clinicalHistory || ''}
            onChange={(e) => {
              change({ clinicalHistory: e.target.value })
              prefetchOnSettle(e.target.value)
            }}
            onKeyDown={moveInColumn}
            // Back to the start of the text, so the cell previews its first words.
            onBlur={(e) => {
              e.target.scrollLeft = 0
              e.target.scrollTop = 0
            }}
            placeholder={aiOn && !hasHistory(sample) ? 'Needed for AI' : ''}
            inputProps={{
              id: historyInputId,
              'aria-label': `Clinical history of ${sample.key}`,
              'data-col': 'history',
              'data-row': index,
            }}
            sx={{
              ...inputSx,
              py: 0,
              '& textarea': {
                p: 0,
                height: '20px !important',
                lineHeight: '20px',
                resize: 'none',
                overflow: 'hidden',
                whiteSpace: 'pre',
              },
            }}
          />
        )
      case 'credits': {
        const price = priceOf(workflow, sample, prices)
        const known = price.credits !== null
        const title = !known
          ? UNKNOWN_PRICE_TEXT
          : blocked
          ? `${partsText(price.parts)}. Not in the total until it is ready.`
          : partsText(price.parts)
        return (
          <Tooltip title={title} placement="bottom-end">
            <Typography
              aria-label={`Credits for ${sample.key}: ${known ? formatCredits(price.credits) : 'confirmed at start'}`}
              noWrap
              sx={{
                width: '100%',
                textAlign: 'right',
                fontSize: 13.5,
                fontVariantNumeric: 'tabular-nums',
                color: known && !blocked ? 'text.primary' : 'text.secondary',
              }}
            >
              {known ? formatCredits(price.credits) : '?'}
            </Typography>
          </Tooltip>
        )
      }
      case 'hpo':
        return (
          <ButtonBase
            onClick={() => openDetails(`hpo-input-${sample.uid}`)}
            aria-label={`Phenotype of ${sample.key}: ${phenotype || 'none'}`}
            sx={buttonSx}
          >
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: 13.5,
                fontWeight: phenotype ? 400 : 500,
                color: phenotype ? 'text.primary' : 'primary.main',
              }}
            >
              {phenotype || 'Add'}
            </Typography>
          </ButtonBase>
        )
      case 'tumour':
        return (
          <ButtonBase
            onClick={() => openDetails(`tumour-input-${sample.uid}`)}
            aria-label={`Tumour type of ${sample.key}: ${tumour ? tumour.name : 'none'}`}
            sx={buttonSx}
          >
            <Typography
              component="span"
              noWrap
              sx={{
                fontSize: 13.5,
                fontFamily: tumour ? 'fontFamilyMono' : undefined,
                color: sample.tumourType ? 'text.primary' : 'text.secondary',
              }}
            >
              {tumour ? tumour.oncotree_code : 'None'}
            </Typography>
          </ButtonBase>
        )
      case 'notes':
        return (
          <InputBase
            value={sample.notes || ''}
            onChange={(e) => setPatient('notes', e.target.value)}
            onKeyDown={moveInColumn}
            placeholder=""
            disabled={!patientEditable}
            inputProps={{
              'aria-label': `Notes for ${sample.key}`,
              maxLength: NOTES_MAX_LENGTH,
              'data-col': 'notes',
              'data-row': index,
            }}
            sx={inputSx}
          />
        )
      case 'expand':
        return (
          <>
            <Tooltip title="Details">
              <ButtonBase
                onClick={() => onToggleExpand(sample.uid)}
                aria-expanded={expanded}
                aria-label={`Details of ${sample.key}`}
                sx={iconButtonSx}
              >
                <ExpandIcon
                  sx={{
                    fontSize: 18,
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: theme.transitions.create('transform', { duration: 150 }),
                  }}
                />
              </ButtonBase>
            </Tooltip>
            {/* One click takes the sample out of the batch (the panel offers Undo).
                Shown on hover and focus, like Home's "Not for analysis". */}
            <Tooltip title="Remove from batch">
              <ButtonBase
                className="row-remove"
                onClick={() => onRemove(sample.uid)}
                aria-label={`Remove ${sample.key}`}
                sx={iconButtonSx}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </ButtonBase>
            </Tooltip>
          </>
        )
      default:
        return null
    }
  }

  return (
    <Box
      role="row"
      sx={{
        borderRadius: `${theme.shape.borderRadius}px`,
        '&:hover': { backgroundColor: expanded ? 'transparent' : 'action.hover' },
        ...(expanded && { boxShadow: `inset 0 0 0 1px ${theme.palette.divider}` }),
        '& .row-remove': {
          opacity: expanded ? 1 : 0,
          transition: theme.transitions.create('opacity', { duration: 120 }),
        },
        '&:hover .row-remove, &:focus-within .row-remove': { opacity: 1 },
        '@media (hover: none)': { '& .row-remove': { opacity: 1 } },
      }}
    >
      <Box
        {...{ [CELL_ROW_ATTR]: '' }}
        sx={{
          display: 'grid',
          gridTemplateColumns: grid,
          columnGap: `${COLUMN_GAP_PX}px`,
          alignItems: 'center',
          minHeight: 44,
          px: `${ROW_PX}px`,
        }}
      >
        {cols.map((col) => (
          <Box key={col.id} role="cell" sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
            {cell(col)}
          </Box>
        ))}
      </Box>
      {lineIssues.length > 0 && (
        <IssueList issues={lineIssues} onFix={fixIssue} sx={{ pl: `${SAMPLE_START_PX}px`, pr: 1, pb: 1, mt: -0.5 }} />
      )}
      {aiColumns && (
        <Popover
          open={hpoOpen && Boolean(hpoCellRef.current)}
          anchorEl={hpoCellRef.current}
          onClose={() => setHpoOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          TransitionProps={{ onEntered: () => document.getElementById(hpoCellInputId)?.focus() }}
          PaperProps={{ sx: { p: 2, width: 420, maxWidth: 'calc(100vw - 32px)', borderRadius: '10px', mt: 0.75 } }}
        >
          <GroupLabel>
            HPO terms for{' '}
            <Box component="span" sx={{ ...MONO_SX, fontSize: 13, color: 'text.primary' }}>
              {sample.key}
            </Box>
          </GroupLabel>
          <HPOFilterWrapper
            value={hpoTerms}
            onChange={({ hpoTerms: terms }) => change({ hpo: fromTerms(terms) })}
            arrayOutput
            label=""
            compact
            inputId={hpoCellInputId}
          />
          {/* With the AI columns the clinical history is a cell of the row, so its suggestions show here. */}
          <HpoSuggestions
            text={sample.clinicalHistory}
            onTextChange={(clinicalHistory) => change({ clinicalHistory })}
            hpo={sample.hpo}
            onChange={(hpo) => change({ hpo })}
            scope={sample.uid}
            label={sample.key}
            sx={{ mt: 1.5 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <ResultButton onClick={() => setHpoOpen(false)}>Done</ResultButton>
          </Box>
        </Popover>
      )}
      {expanded && (
        <Box sx={{ pl: `${SAMPLE_START_PX}px`, pr: 2, pt: 1, pb: 2.5 }}>
          {longRead && (
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.5 }}>
                <GroupLabel sx={{ mb: 0 }}>Options for this sample</GroupLabel>
                {custom && <TextLink onClick={() => change({ longRead: null })}>Use batch options</TextLink>}
              </Box>
              <LongReadOptions workflow={workflow} sample={sample} onChange={change} />
            </Box>
          )}
          <SampleEditor
            inline
            inlineNotes={aiColumns}
            sample={sample}
            workflow={workflow}
            fileMap={fileMap}
            onChange={change}
            normalOptions={normalOptions}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            {somaticRow && (
              <TextLink onClick={() => setDetailsOpen(true)} data-testid={`sample-details-${sample.uid}`}>
                Specimen and library details
              </TextLink>
            )}
            <TextLink tone="muted" onClick={() => onToggleExpand(sample.uid)}>
              Done
            </TextLink>
          </Box>
        </Box>
      )}
      {detailsOpen && (
        // The editor heads itself with the sample name, so the dialog has no title of its own.
        <Dialog open onClose={() => setDetailsOpen(false)} fullWidth maxWidth="md" aria-label={sample.key}>
          <DialogContent>
            <SampleEditor
              sample={sample}
              workflow={workflow}
              fileMap={fileMap}
              onChange={change}
              issues={issues.map((i) => ({ ...i, fixes: (i.fixes || []).filter((f) => f !== 'details') }))}
              onFix={fixIssue}
              normalOptions={normalOptions}
              bedFiles={bedFiles}
              rnaCapture={rnaCapture}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <ResultButton onClick={() => setDetailsOpen(false)}>Done</ResultButton>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  )
})

// ----------------------------------------------------------------------
// Bulk actions for the selected rows. Each opens a small popover; no menus of
// actions.

const BulkPopover = ({ anchor, onClose, children, width = 280, align = 'left' }) => (
  <Popover
    open={!!anchor}
    anchorEl={anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: align }}
    transformOrigin={{ vertical: 'top', horizontal: align }}
    PaperProps={{ sx: { p: 2, width, borderRadius: '10px', mt: 0.75 } }}
  >
    {children}
  </Popover>
)

// "2 long-read samples keep their saved details." for a bulk Set sex or Set age.
export const skippedPatientNote = (n) =>
  n ? `${n} long-read ${n === 1 ? 'sample keeps its' : 'samples keep their'} saved details.` : ''

const SelectionBar = ({ count, editable, canMerge, workflow, ai, onSetAI, onApply, onRemove, onClear, onMerge }) => {
  const [open, setOpen] = useState(null) // { kind, anchor }
  const [age, setAge] = useState('')
  const [terms, setTerms] = useState([])
  const [tumour, setTumour] = useState(null)
  const somatic = workflow.analysis === 'somatic'
  const close = () => setOpen(null)
  const openFor = (kind) => (e) => setOpen({ kind, anchor: e.currentTarget })
  const parsedAge = parseAge(age)
  const ageValid = typeof parsedAge === 'number' && !Number.isNaN(parsedAge)
  const applyAge = () => {
    if (!ageValid) return
    onApply({ patient: { age: parsedAge } })
    setAge('')
    close()
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 40 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, mr: 1 }}>{count} selected</Typography>
      {/* Long-read rows keep their saved details; the line under the bar says so. */}
      <ResultButton onClick={openFor('sex')} disabled={!editable}>
        Set sex
      </ResultButton>
      <ResultButton onClick={openFor('age')} disabled={!editable}>
        Set age
      </ResultButton>
      {somatic ? (
        <ResultButton onClick={openFor('tumour')}>Set tumour type</ResultButton>
      ) : (
        <ResultButton onClick={openFor('hpo')}>Add HPO</ResultButton>
      )}
      {ai && (
        <>
          <ResultButton onClick={() => onSetAI(true)} disabled={!ai.canOn}>
            AI on
          </ResultButton>
          <ResultButton onClick={() => onSetAI(false)} disabled={!ai.canOff}>
            AI off
          </ResultButton>
        </>
      )}
      {canMerge && <ResultButton onClick={onMerge}>Merge into one sample</ResultButton>}
      <ResultButton onClick={onRemove}>Remove</ResultButton>
      <TextLink tone="muted" onClick={onClear} sx={{ ml: 1 }}>
        Clear
      </TextLink>

      <BulkPopover anchor={open?.kind === 'sex' ? open.anchor : null} onClose={close} width={220}>
        {SEX_OPTIONS.map((o) => (
          <MenuItem
            key={o.value || 'unknown'}
            sx={{ fontSize: 14, borderRadius: 1 }}
            onClick={() => {
              onApply({ patient: { sex: o.value } })
              close()
            }}
          >
            {o.label}
          </MenuItem>
        ))}
      </BulkPopover>

      <BulkPopover anchor={open?.kind === 'age' ? open.anchor : null} onClose={close} width={220}>
        <Muted sx={{ mb: 1 }}>Age in years</Muted>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <NumberInput
            value={age}
            onChange={setAge}
            placeholder="Years"
            inputProps={{ autoFocus: true, 'aria-label': 'Age in years' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyAge()
            }}
          />
          <ResultButton primary disabled={!ageValid} sx={{ height: 36 }} onClick={applyAge}>
            Apply
          </ResultButton>
        </Box>
      </BulkPopover>

      <BulkPopover anchor={open?.kind === 'hpo' ? open.anchor : null} onClose={close} width={420}>
        <Muted sx={{ mb: 1 }}>Added to every selected sample</Muted>
        <HPOFilterWrapper
          value={terms}
          onChange={({ hpoTerms }) => setTerms(hpoTerms || [])}
          arrayOutput
          label=""
          compact
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <ResultButton
            primary
            disabled={!terms.length}
            onClick={() => {
              onApply({ addHpo: fromTerms(terms) })
              setTerms([])
              close()
            }}
          >
            Add {terms.length || ''} HPO
          </ResultButton>
        </Box>
      </BulkPopover>

      <BulkPopover anchor={open?.kind === 'tumour' ? open.anchor : null} onClose={close} width={360}>
        <TumourTypeSelect value={tumour} onChange={setTumour} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <ResultButton
            primary
            onClick={() => {
              onApply({ tumourType: tumour })
              setTumour(null)
              close()
            }}
          >
            {tumour ? 'Apply' : 'Use batch default'}
          </ResultButton>
        </Box>
      </BulkPopover>
    </Box>
  )
}

// The sample sheet template, prefilled with this batch's samples: Excel or CSV.
const TEMPLATE_CHOICES = [
  { format: 'xlsx', label: 'Excel template' },
  { format: 'csv', label: 'CSV template' },
]

const TemplateButton = ({ onPick }) => {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <ResultButton onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="true" aria-expanded={!!anchor}>
        Template
      </ResultButton>
      <BulkPopover anchor={anchor} onClose={() => setAnchor(null)} width={240} align="right">
        <Muted sx={{ mb: 1 }}>One row per sample in this batch.</Muted>
        {TEMPLATE_CHOICES.map((choice) => (
          <MenuItem
            key={choice.format}
            sx={{ fontSize: 14, borderRadius: 1 }}
            onClick={() => {
              setAnchor(null)
              onPick(choice.format)
            }}
          >
            {choice.label}
          </MenuItem>
        ))}
      </BulkPopover>
    </>
  )
}

// ----------------------------------------------------------------------

const FILTERS = ['all', 'attention', 'ready']

const SampleTable = ({
  rows,
  workflow,
  fileMap,
  issuesByUid,
  onChangeSample,
  onBulk,
  onRemove,
  onFix,
  onMerge,
  normalOptionsFor,
  onImportSheet,
  onAddFiles,
  sheetNote,
  expandedUid,
  onExpand,
  prices: givenPrices,
  bedFiles,
  rnaCapture,
}) => {
  const theme = useTheme()
  const [filter, setFilter] = useState('all')
  // The row being edited (open, or its HPO terms open) stays in a filtered view
  // until the edit ends, so giving it what it needed does not pull it away mid-edit
  // (and close the HPO graph on the first term added). Another view lets it go.
  const [held, setHeld] = useState(() => ({ expanded: expandedUid, hpo: null }))
  useEffect(() => {
    setHeld((h) => (h.expanded === expandedUid ? h : { ...h, expanded: expandedUid }))
  }, [expandedUid])
  const holdHpo = useCallback(
    (uid, open) => setHeld((h) => (open ? { ...h, hpo: uid } : h.hpo === uid ? { ...h, hpo: null } : h)),
    [],
  )
  const chooseFilter = (next) => {
    setFilter(next)
    setHeld({ expanded: null, hpo: null })
  }
  const [selected, setSelected] = useState(() => new Set())
  // The panel's prices (model/pricing usePrices); read here when none are given.
  const fetchedPrices = usePrices()
  const prices = givenPrices === undefined ? fetchedPrices : givenPrices
  const [templateError, setTemplateError] = useState('')

  // The AI columns show while any row runs AI or the batch is AI-assisted. Once
  // shown, they stay until the batch's analysis or interpretation changes, so
  // unticking the last AI row does not pull the column from under the pointer.
  const germline = workflow.analysis === 'germline'
  const anyCanUseAI = useMemo(() => germline && rows.some((s) => canUseAI(workflow, s)), [germline, rows, workflow])
  const aiWanted = anyCanUseAI && (workflow.interpretation === 'ai' || rows.some((s) => effectiveAI(workflow, s)))
  const aiKey = `${workflow.analysis}:${workflow.interpretation}`
  const [aiShownFor, setAiShownFor] = useState(null)
  useEffect(() => {
    if (aiWanted) setAiShownFor(aiKey)
  }, [aiWanted, aiKey])
  const aiColumns = aiWanted || (anyCanUseAI && aiShownFor === aiKey)

  const cols = useMemo(
    () => columnsFor(workflow, { ai: aiColumns, credits: prices !== null }),
    [workflow, aiColumns, prices],
  )
  const grid = useMemo(() => gridOf(cols), [cols])

  const counts = useMemo(() => {
    let attention = 0
    let ready = 0
    rows.forEach((s) => {
      const issues = issuesByUid.get(s.uid) || []
      if (issues.length) attention += 1
      if (!issues.some((i) => i.blocking)) ready += 1
    })
    return { all: rows.length, attention, ready }
  }, [rows, issuesByUid])

  const shown = useMemo(() => {
    if (filter === 'all') return rows
    return rows.filter((s) => {
      if (s.uid === held.expanded || s.uid === held.hpo) return true
      const issues = issuesByUid.get(s.uid) || []
      return filter === 'attention' ? issues.length > 0 : !issues.some((i) => i.blocking)
    })
  }, [rows, filter, issuesByUid, held])

  const selectedRows = useMemo(() => rows.filter((s) => selected.has(s.uid)), [rows, selected])
  const selectedInRows = useMemo(() => selectedRows.map((s) => s.uid), [selectedRows])
  const editableCount = selectedRows.filter(canSavePatient).length
  const skippedCount = selectedRows.length - editableCount
  // Demultiplexed lanes of one sample, uploaded under different names, as the
  // wizard's "Demultiplexed Sample": germline and somatic DNA FASTQ.
  const canMerge =
    Boolean(onMerge) && lanesAllowed(workflow) && selectedRows.filter((s) => s.fileType === 'fastq').length >= 2
  const allShownSelected = shown.length > 0 && shown.every((s) => selected.has(s.uid))

  // AI for rows: one row, every shown row (the column header), or the selection.
  const setAI = (samples, on) =>
    samples
      .filter((s) => canUseAI(workflow, s) && effectiveAI(workflow, s) !== on)
      .forEach((s) => onChangeSample(s.uid, aiPatch(workflow, on)))
  const shownCanUseAI = shown.filter((s) => canUseAI(workflow, s))
  const shownWithAI = shownCanUseAI.filter((s) => effectiveAI(workflow, s)).length
  const allShownAI = shownCanUseAI.length > 0 && shownWithAI === shownCanUseAI.length
  const selectedCanUseAI = selectedRows.filter((s) => canUseAI(workflow, s))
  const selectionAI = selectedCanUseAI.length
    ? {
        canOn: selectedCanUseAI.some((s) => !effectiveAI(workflow, s)),
        canOff: selectedCanUseAI.some((s) => effectiveAI(workflow, s)),
      }
    : null
  // AI rows still missing HPO terms or clinical history.
  const aiMissing = aiColumns ? rows.filter((s) => effectiveAI(workflow, s) && !hasPhenotype(s)).length : 0

  const pickTemplate = (format) => {
    setTemplateError('')
    Promise.resolve()
      .then(() => downloadTemplate(format, rows, workflow))
      .catch(() =>
        setTemplateError(
          format === 'xlsx' ? 'The Excel template could not be made. Try the CSV template.' : 'Download failed.',
        ),
      )
  }

  const toggleSelect = useCallback(
    (uid) =>
      setSelected((prev) => {
        const next = new Set(prev)
        if (next.has(uid)) next.delete(uid)
        else next.add(uid)
        return next
      }),
    [],
  )
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allShownSelected) shown.forEach((s) => next.delete(s.uid))
      else shown.forEach((s) => next.add(s.uid))
      return next
    })
  const toggleExpand = useCallback((uid) => onExpand((current) => (current === uid ? null : uid)), [onExpand])
  const removeOne = useCallback(
    (uid) => {
      onRemove([uid])
      setSelected((prev) => {
        if (!prev.has(uid)) return prev
        const next = new Set(prev)
        next.delete(uid)
        return next
      })
    },
    [onRemove],
  )

  const filterOptions = [
    { value: 'all', label: `All ${counts.all}` },
    { value: 'attention', label: `Needs attention ${counts.attention}` },
    { value: 'ready', label: `Ready ${counts.ready}` },
  ].filter((o) => FILTERS.includes(o.value))

  return (
    <Box {...{ [TABLE_ATTR]: '' }} sx={{ minWidth: 640 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, minHeight: 40, flexWrap: 'wrap' }}>
        {selectedInRows.length ? (
          <SelectionBar
            count={selectedInRows.length}
            editable={editableCount > 0}
            canMerge={canMerge}
            workflow={workflow}
            ai={selectionAI}
            onSetAI={(on) => setAI(selectedRows, on)}
            onApply={(action) => onBulk(selectedInRows, action)}
            onRemove={() => {
              onRemove(selectedInRows)
              setSelected(new Set())
            }}
            onMerge={() => {
              onMerge(selectedInRows)
              setSelected(new Set())
            }}
            onClear={() => setSelected(new Set())}
          />
        ) : (
          <>
            <Segmented value={filter} options={filterOptions} onChange={chooseFilter} fullWidth={false} size="small" />
            <Box sx={{ flex: 1 }} />
            <ResultButton onClick={onImportSheet}>Import sheet</ResultButton>
            <TemplateButton onPick={pickTemplate} />
            <ResultButton onClick={onAddFiles}>Add files</ResultButton>
          </>
        )}
      </Box>
      {selectedInRows.length > 0 && skippedCount > 0 && (
        <Muted sx={{ mb: 1.5, mt: -0.5 }}>{skippedPatientNote(skippedCount)}</Muted>
      )}
      {templateError && (
        <Muted sx={{ mb: 1.5, mt: -0.5 }}>
          {templateError}{' '}
          <TextLink tone="muted" onClick={() => setTemplateError('')}>
            Dismiss
          </TextLink>
        </Muted>
      )}
      {sheetNote && <Box sx={{ mb: 1.5 }}>{sheetNote}</Box>}
      {aiColumns && (
        <Muted sx={{ mb: 1.5 }}>
          {AI_HINT}
          {aiMissing > 0 && (
            <>
              {` ${aiMissing} still ${aiMissing === 1 ? 'needs' : 'need'} them.`}
              {filter !== 'attention' && (
                <>
                  {' '}
                  <TextLink onClick={() => chooseFilter('attention')}>Show</TextLink>
                </>
              )}
            </>
          )}
        </Muted>
      )}

      <Box
        role="row"
        sx={{
          display: 'grid',
          gridTemplateColumns: grid,
          columnGap: `${COLUMN_GAP_PX}px`,
          alignItems: 'center',
          height: 36,
          px: `${ROW_PX}px`,
          position: 'sticky',
          top: 0,
          zIndex: 2,
          backgroundColor: 'background.paper',
          boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
        }}
      >
        {cols.map((col) => (
          <Box key={col.id} role="columnheader" sx={{ minWidth: 0 }}>
            {col.id === 'select' ? (
              <Checkbox
                size="small"
                checked={allShownSelected}
                indeterminate={!allShownSelected && shown.some((s) => selected.has(s.uid))}
                onChange={toggleAll}
                inputProps={{ 'aria-label': 'Select all shown' }}
                sx={{ p: 0.5 }}
              />
            ) : col.id === 'ai' ? (
              <Box component="label" sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <Checkbox
                  size="small"
                  checked={allShownAI}
                  indeterminate={!allShownAI && shownWithAI > 0}
                  disabled={!shownCanUseAI.length}
                  onChange={() => setAI(shownCanUseAI, !allShownAI)}
                  inputProps={{ 'aria-label': 'AI for all shown samples' }}
                  sx={{ p: 0.5 }}
                />
                <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}>
                  AI
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'text.secondary',
                  pl: PADDED_HEADERS.has(col.id) ? '7px' : 0,
                  textAlign: RIGHT_HEADERS.has(col.id) ? 'right' : 'left',
                }}
                noWrap
              >
                {col.label || ''}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box role="rowgroup" sx={{ pt: 0.5 }}>
        {shown.map((sample, index) => (
          <SampleRow
            key={sample.uid}
            sample={sample}
            index={index}
            cols={cols}
            grid={grid}
            workflow={workflow}
            fileMap={fileMap}
            issues={issuesByUid.get(sample.uid) || NO_ISSUES}
            selected={selected.has(sample.uid)}
            expanded={expandedUid === sample.uid}
            onToggleSelect={toggleSelect}
            onToggleExpand={toggleExpand}
            onRemove={removeOne}
            onChange={onChangeSample}
            onFix={onFix}
            normalOptions={normalOptionsFor ? normalOptionsFor(sample) : NO_OPTIONS}
            prices={prices}
            aiColumns={aiColumns}
            onHoldHpo={holdHpo}
            bedFiles={bedFiles}
            rnaCapture={rnaCapture}
          />
        ))}
        {shown.length === 0 && <Muted sx={{ px: 1, py: 3 }}>No samples in this view.</Muted>}
      </Box>
    </Box>
  )
}

export default SampleTable
